import React from 'react';
import { Text, Flex, Box } from '@chakra-ui/react';
import { ButtonText, Badge, OutlineCard } from '@morressier/alembic';
import { IAuthorWithEmail } from '@publishing-engine/domain/models';
import { ISubmissionAuthor } from 'src/contexts/FormContext';
import Divider from 'src/components/Divider';
import AuthorDetailsModal from '../AuthorsTab/AuthorDetailsModal';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import {
  FormFieldAuthors,
  FormFieldContactAuthor,
  FormFieldCorrespondingAuthor,
  FormFieldSubmittingAuthor
} from '@publishing-engine/domain/types/article';
import { GenericSubmissionField } from './FormFieldGeneric/GenericSubmissionField';
import {
  groupAuthorsByRoles,
  IAuthorRolesMap
} from 'src/utils/groupAuthorByRoles';

interface Props {
  showTitle?: boolean;
  isUpdateRequired?: boolean;
  isRevisionReview?: boolean;
  isRevision?: boolean;
}
export const AuthorsTab: React.FC<Props> = ({
  showTitle = true,
  isUpdateRequired = true,
  isRevisionReview = false,
  isRevision = false
}) => {
  const { getFieldState } = useSubmissionFields();
  const authors = getFieldState<FormFieldAuthors>('Authors')?.value;
  const submittingAuthorEmail =
    getFieldState<FormFieldSubmittingAuthor>('SubmittingAuthor')?.value!;
  const contactAuthorEmail =
    getFieldState<FormFieldContactAuthor>('ContactAuthor')?.value;
  const correspondingAuthorEmail = getFieldState<FormFieldCorrespondingAuthor>(
    'CorrespondingAuthor'
  )?.value;

  const roles: IAuthorRolesMap = {
    Submitting: submittingAuthorEmail,
    ...(contactAuthorEmail && {
      Contact: contactAuthorEmail
    }),
    ...(correspondingAuthorEmail && {
      Corresponding: correspondingAuthorEmail
    })
  };

  const [selectedAuthor, setSelectedAuthor] = React.useState<
    IAuthorWithEmail | ISubmissionAuthor | null
  >(null);

  const AuthorCard = ({
    author,
    roles
  }: {
    author: IAuthorWithEmail | ISubmissionAuthor;
    roles: string[];
  }) => {
    return (
      <Flex flexDirection="column" gap={4} bgColor="surfacePrimaryFill">
        {roles.length && (
          <>
            <Flex gap={4}>
              {roles?.map(role => (
                <Badge variant="attention" size="context">
                  {role} Author
                </Badge>
              ))}
            </Flex>
          </>
        )}
        {/* Avatar */}
        <Flex gap={4}>
          <Flex flexDirection="column" flex={1}>
            <Text textStyle="headingItem">
              {[author.firstName, author.middleName, author.lastName]
                .filter(Boolean)
                .join(' ')}
            </Text>
            <Text textStyle="textBody">
              {author.affiliation}
              {author.country && `, ${author.country}`}
            </Text>
            <Text textStyle="textBody">{author.email}</Text>
          </Flex>

          <ButtonText
            alignSelf="center"
            onClick={() => setSelectedAuthor(author)}
          >
            View Details
          </ButtonText>
        </Flex>
      </Flex>
    );
  };

  const renderAuthors = () =>
    groupAuthorsByRoles(roles, authors).map((author, i) => (
      <>
        <AuthorCard key={author.email} author={author} roles={author.roles} />
        {i !== groupAuthorsByRoles(roles, authors).length - 1 && (
          <Box my={4}>{Divider}</Box>
        )}
      </>
    ));

  return (
    <>
      {selectedAuthor ? (
        <AuthorDetailsModal
          author={selectedAuthor}
          onBackClick={() => setSelectedAuthor(null)}
        />
      ) : null}

      <Flex gap={6} flexDirection="column">
        {showTitle && (
          <>
            <Flex flexDirection="column" alignItems="flex-start" gap={2}>
              <Text textStyle="headingSection">Authors</Text>
              <Text textStyle="textBody">
                Thoroughly review the information for all authors in this
                submission.
              </Text>
            </Flex>

            <Text textStyle="headingGroup" id="Author Roles">
              Author Roles
            </Text>
            <OutlineCard padding={4}>{renderAuthors()}</OutlineCard>
          </>
        )}

        <GenericSubmissionField
          fieldName="Authors"
          isUpdateRequired={isUpdateRequired}
          isRevisionReview={isRevisionReview}
          isRevision={isRevision}
        />
        {!isRevisionReview && (
          <Flex flexDirection="column">
            <Text textStyle="headingGroup" id="Submission management">
              Submission management
            </Text>
            <Text textStyle="textBody">
              Allocate authors or representatives to specific roles for
              efficient submission management.
            </Text>
          </Flex>
        )}

        <GenericSubmissionField
          fieldName="SubmittingAuthor"
          isUpdateRequired={isUpdateRequired}
          isRevisionReview={isRevisionReview}
        />

        <GenericSubmissionField
          fieldName="CorrespondingAuthor"
          isUpdateRequired={isUpdateRequired}
          isRevisionReview={isRevisionReview}
        />
      </Flex>
    </>
  );
};
